/**
 * The order-flow kind toggles in the TradeFinder panel's Signals tab, in
 * order. State lives in levelsStore (`flow*` keys); `kind` picks the glyph
 * from FLOW_GLYPHS so the switch shows the same mark as the chart.
 *
 * Descriptions carry no threshold numbers of their own: the thresholds are
 * per root, user-editable and self-tuned, so a hard-coded default would lie.
 * `detailed` names the flow-signals settings paths (`{path}`) whose effective
 * values for the chart's root fill it in (describeFlowSignal); `description`
 * is the numberless fallback until those settings are loaded.
 */
import type { FlowLevelKey } from '../../store/flowSignalsStore';
import type { FlowLayer } from './flowSettings';

export interface FlowSignalConfig {
  key: FlowLevelKey;
  /** The kind whose glyph the switch shows. */
  kind: string;
  label: string;
  /** Numberless: always true whatever the settings. */
  description: string;
  /** With `{settings.path}` placeholders, filled from the root's effective settings. */
  detailed?: string;
}

export const FLOW_SIGNAL_CONFIGS: FlowSignalConfig[] = [
  {
    key: 'flowAbsorption', kind: 'absorption', label: 'Absorption',
    description: 'Aggressive orders absorbed by resting size: Buy Absorption is bearish (above the price), Sell Absorption bullish (below)',
    detailed: 'Aggressive orders ({absorption.trigger}+ lots within {absorption.duration_s}s) absorbed by resting size: Buy Absorption is bearish (above the price), Sell Absorption bullish (below)',
  },
  {
    key: 'flowSweep', kind: 'sweep', label: 'Sweeps',
    description: 'One aggressive order through many levels, incl. Sweep-S (a triggered stop swept); faint = passive / stop sweep traces',
    detailed: 'One aggressive order through {sweep.price_range_levels}+ levels and {sweep.trigger_volume}+ lots, incl. Sweep-S (a triggered stop swept); faint = passive / stop sweep traces',
  },
  {
    key: 'flowStopRun', kind: 'stop_run', label: 'Stop runs',
    description: 'A chain of same-side triggered stops over several levels',
    detailed: 'A chain of same-side triggered stops ({stops.run_trigger_volume}+ lots over {stops.run_price_range_levels}+ levels)',
  },
  {
    // The 10% pull-back is fixed in flow-signals (no setting), so it stays in both.
    key: 'flowStopVolume', kind: 'stop_volume', label: 'Stop volume',
    description: 'The stop-volume line fell 10% from a peak above its signal level',
    detailed: 'The stop-volume line fell 10% from a peak above its signal level ({stops.signal_level} lots)',
  },
  {
    key: 'flowVolumeSpike', kind: 'volume_spike', label: 'Volume spikes',
    description: 'One-sided burst of aggressive volume moving price several levels',
    detailed: 'One-sided burst of aggressive volume ({volume_spike.trigger}+ lots within {volume_spike.duration_s}s) moving price {volume_spike.price_range}+ levels',
  },
  {
    key: 'flowImbalance', kind: 'imbalance_signal', label: 'Market volume imbalance',
    description: 'Decayed buy-minus-sell volume crossing its trigger',
    detailed: 'Decayed buy-minus-sell volume crossing its trigger (signal 1: {imbalance.signal1.buy_trigger} lots)',
  },
  {
    key: 'flowIceberg', kind: 'iceberg', label: 'Icebergs',
    description: 'Hidden orders refilling at one price: Iceberg Bid bullish (below), Ask bearish (above); outline = developing or cancelled',
    detailed: 'Hidden orders ({iceberg.trigger}+ lots) refilling at one price: Iceberg Bid bullish (below), Ask bearish (above); outline = developing or cancelled',
  },
  {
    key: 'flowBookImbalance', kind: 'book_imbalance', label: 'Book imbalance',
    description: 'Resting depth heavier on one side: bid side bullish (below), ask side bearish (above)',
    detailed: 'Resting depth heavier on one side by {book_imbalance.imbalance1.trigger_pct}%+: bid side bullish (below), ask side bearish (above)',
  },
  {
    key: 'flowLiquidity', kind: 'liquidity', label: 'Liquidity changes',
    description: 'Large resting size added or removed: bid side below the price, ask side above',
    detailed: 'Large resting size added or removed ({liquidity.relative_trigger}+ lots in one book update, or a level reaching {liquidity.absolute_trigger}+ lots): bid side below the price, ask side above',
  },
];

/**
 * Context: research-backed reads shown under their own heading. Display only:
 * none of them alerts, pushes or chimes. Their descriptions quote the
 * full-history research numbers (/mnt/Fast/mbo-research/report.md, 2024-2025
 * MBO), which are facts about the past sample, not settings, so they are not
 * placeholders. Each kind only has data on the roots flow-signals runs it for
 * (`all_roots` in the Settings tab extends that).
 */
export type FlowContextKey =
  | 'flowQueueTilt'
  | 'flowQueueDepletion'
  | 'flowBurstLevel'
  | 'flowAbsBreakLevel'
  | 'flowSweepFollow';

export interface FlowContextConfig {
  key: FlowContextKey;
  /** Glyph on the switch (the chart's own for marker kinds). */
  glyph: string;
  label: string;
  description: string;
  detailed?: string;
}

export const FLOW_CONTEXT_CONFIGS: FlowContextConfig[] = [
  {
    key: 'flowQueueTilt', glyph: '⇅', label: 'Queue tilt (ES/MES)',
    description: 'Top-of-book queue tilt: a ribbon along the bottom of the chart (one cell per bar) and a band at the price scale; '
      + 'green = bid queue heavier, red = ask, stronger colour = stronger tilt. The thin queue tends to get eaten next: '
      + 'on 2024-2025 history the next 5 s went the tilt\'s way 67% of the time on ES, 66% on MES. MES charts show MES\'s own book '
      + 'with ES\'s as a thinner second row (61% on MES). About half a tick: context, not a signal.',
  },
  {
    key: 'flowQueueDepletion', glyph: '⌃', label: 'Queue depletion (ES/MES)',
    description: 'The best bid/ask queue built up to 2x its usual size, then got eaten down to 20% of that by trades while the other side stays thick. '
      + 'The next tick tends to go through: 2024-2025 +0.7 ticks (ES) / +0.6 (MES) over the next 60 s. Context, not a signal.',
  },
  {
    key: 'flowBurstLevel', glyph: '⇈', label: 'Burst into level (NQ/MNQ)',
    description: 'A 1 s burst of aggressive buying or selling (4x the usual pace) with HOD/LOD/ONH/ONL up to 8 ticks ahead. '
      + "Break likely, don't fade it: 2024-2025 +1.1 ticks (NQ) / +1.5 (MNQ) over the next 60 s in the push's direction, both years. "
      + 'Context, not a signal.',
  },
  {
    key: 'flowAbsBreakLevel', glyph: '▲', label: 'Absorption break at level (NQ/MNQ)',
    description: 'Sellers (buyers) absorbed a lot of buying (selling) at one price, then the price broke through it, within 8 ticks of VWAP/HOD/LOD/ONH/ONL. '
      + 'The break tended to carry: 2024-2025 +1.0 tick (NQ) / +1.5 (MNQ) over the next 60 s. Context, not a signal.',
  },
  {
    key: 'flowSweepFollow', glyph: '⏩', label: 'Sweep follow-through (unproven)',
    description: 'A 4-5+ level sweep that keeps extending (+2 ticks within 10 s). Unproven: on 2024-2025 history the whole move came '
      + 'in the first 5 s and then faded, which a stale quote can also produce. Kept for re-testing on new data; off by default.',
    // min_levels is 0 (= auto, 4-5 by root) by default, so only the extension rule is filled in.
    detailed: 'A 4-5+ level sweep that keeps extending (+{sweep_follow.extend_ticks} ticks within {sweep_follow.window_s} s). Unproven: '
      + 'on 2024-2025 history the whole move came in the first 5 s and then faded, which a stale quote can also produce. '
      + 'Kept for re-testing on new data; off by default.',
  },
];

/** 1000 -> "1,000", 0.5 -> "0.5". */
function fmtSetting(v: number): string {
  return Number.isInteger(v) ? v.toLocaleString('en-US') : String(Math.round(v * 100) / 100);
}

/**
 * The switch description for one kind: `detailed` with every `{path}` set
 * to that path's effective value for the root, or the numberless
 * `description` when settings are not loaded or any value is missing.
 */
export function describeFlowSignal(
  cfg: Pick<FlowSignalConfig, 'description' | 'detailed'>,
  layers: Record<string, Pick<FlowLayer, 'effective'>> | null | undefined,
): string {
  if (!cfg.detailed || !layers) return cfg.description;
  let missing = false;
  const text = cfg.detailed.replace(/\{([a-z0-9_.]+)\}/gi, (_, path: string) => {
    const v = layers[path]?.effective;
    if (typeof v !== 'number' || !Number.isFinite(v)) {
      missing = true;
      return '';
    }
    return fmtSetting(v);
  });
  return missing ? cfg.description : text;
}
